/**
 * فواتير البيع — sales invoices and sales orders as two tabs of one page.
 *
 * They were two sidebar entries, and the sidebar had grown long enough that
 * finding anything in it was the work. These two belong together anyway: an
 * order becomes an invoice, and looking for "that sale" means looking in
 * whichever of the two it has reached.
 *
 * THE URL IS THE TAB, not component state. `/sales/invoices` and
 * `/sales/orders` both still exist and both still land where they always did —
 * which matters far beyond bookmarks: the home "view all" links, the command
 * palette, the storefront order mails and every link already sent to a phone
 * point at those paths. Switching tabs rewrites the URL (replacing, so a tab
 * toggle does not pile up history), so a refresh or a copied link keeps the
 * tab it was on.
 *
 * Each tab is the ordinary DocumentListPage with its own props — no list logic
 * is duplicated or forked here, so both lists keep every filter, export and
 * bulk action they had as separate pages.
 */
import { Receipt, ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/shadcn/index.js';
import { usePermission } from '../../hooks/usePermission.js';

import { DocumentListPage } from './DocumentListPage.js';

type SalesTab = 'invoices' | 'orders';

export function SalesDocumentsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const canInvoices = usePermission('invoices.read');
  const canOrders = usePermission('sales_orders.read');

  // Read from the path, so a deep link decides the tab rather than a default.
  const tab: SalesTab = pathname.startsWith('/sales/orders') ? 'orders' : 'invoices';
  // A reader with only one of the two permissions gets that one, with no tab
  // strip at all — a single tab is a label, not a choice.
  const only = canInvoices !== canOrders;

  const invoiceList = (
    <DocumentListPage
      ns="documents:invoices"
      endpoint="/invoices"
      statusTone={{ draft: 'neutral', issued: 'info', paid: 'success', cancelled: 'danger' }}
      newPath="/sales/invoices/new"
      newModule="manual_sales"
      detailPath="/sales/invoices"
      editable
      printPath="/sales/invoices"
      writePermission="invoices.write"
      postPermission="invoices.post"
    />
  );
  const orderList = (
    // showBilledFilter: an order already turned into an invoice is finished
    // work, so it is hidden here by default and one click away. Only on THIS
    // list — the field-rep orders page shares the endpoint and wants them all.
    <DocumentListPage
      ns="documents:salesOrders"
      endpoint="/sales-orders"
      showBilledFilter
      statusTone={{ draft: 'neutral', submitted: 'info', fulfilled: 'success', cancelled: 'danger' }}
      newPath="/sales/orders/new"
      newModule="manual_sales"
      detailPath="/sales/orders"
      editable
      printPath="/sales/orders"
      writePermission="sales_orders.write"
    />
  );

  if (only) return canInvoices ? invoiceList : orderList;

  return (
    <Tabs
      value={tab}
      onValueChange={(v) => navigate(v === 'orders' ? '/sales/orders' : '/sales/invoices', { replace: true })}
    >
      <TabsList className="mb-4 h-10">
        <TabsTrigger value="invoices" className="gap-1.5">
          <Receipt className="h-4 w-4" aria-hidden />
          {t('nav.invoices', { defaultValue: 'Invoices' })}
        </TabsTrigger>
        <TabsTrigger value="orders" className="gap-1.5">
          <ShoppingBag className="h-4 w-4" aria-hidden />
          {t('nav.salesOrders', { defaultValue: 'Orders' })}
        </TabsTrigger>
      </TabsList>

      {/* Radix unmounts the inactive tab, so only the visible list fetches. */}
      <TabsContent value="invoices">{invoiceList}</TabsContent>
      <TabsContent value="orders">{orderList}</TabsContent>
    </Tabs>
  );
}
